import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useTenants } from '@/api/hooks/useSuperadmin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDate, money } from '@/lib/utils';
import { TenantDetailDialog } from '@/features/superadmin/TenantDetailDialog';

const STATUS_VARIANT = { Active: 'ok', Suspended: 'low', Cancelled: 'out' } as const;

export function TenantsTab() {
  const [params, setParams] = useSearchParams();
  const search = params.get('tsearch') ?? '';
  const status = params.get('tstatus') ?? '';
  const plan = params.get('tplan') ?? '';
  const page = Number(params.get('tpage') ?? '1');

  const debouncedSearch = useDebouncedValue(search, 300);
  const [activeId, setActiveId] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useTenants({
    search: debouncedSearch || undefined,
    status: status || undefined,
    plan: plan || undefined,
    page,
    limit: 25,
  });

  function updateParam(key: string, value: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'tpage') next.delete('tpage');
      return next;
    });
  }

  const tenants = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search name, slug, or owner email…"
          value={search}
          onChange={(e) => updateParam('tsearch', e.target.value)}
          className="max-w-xs"
        />
        <Select value={status || 'all'} onValueChange={(v) => updateParam('tstatus', v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Suspended">Suspended</SelectItem>
            <SelectItem value="Cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={plan || 'all'} onValueChange={(v) => updateParam('tplan', v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            <SelectItem value="Trial">Trial</SelectItem>
            <SelectItem value="Basic">Basic</SelectItem>
            <SelectItem value="Pro">Pro</SelectItem>
            <SelectItem value="Enterprise">Enterprise</SelectItem>
            <SelectItem value="Custom">Custom</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">{data ? `${data.total} pharmacy(ies)` : 'Loading…'}</p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : tenants.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <Building2 className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No pharmacies match these filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" aria-busy={isFetching}>
          {tenants.map((t) => (
            <Card
              key={t.id}
              className="cursor-pointer p-4 transition-colors hover:border-primary/40"
              onClick={() => setActiveId(t.id)}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium">{t.name}</p>
                    <Badge variant={STATUS_VARIANT[t.status]}>{t.status}</Badge>
                    <Badge variant="secondary">{t.plan}</Badge>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {t.slug} · {t.ownerEmail} {t.city ? `· ${t.city}` : ''}
                  </p>
                </div>
                <div className="shrink-0 text-right text-xs text-muted-foreground">
                  <p className="tabular text-sm font-medium text-foreground">{money(t.monthlyFee)}/mo</p>
                  <p>
                    {t.userCount} user(s) ·{' '}
                    {t.daysRemaining != null ? `${t.daysRemaining}d left` : formatDate(t.subscriptionEndsAt)}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => updateParam('tpage', String(page - 1))}
          >
            Previous
          </Button>
          <span className="tabular text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => updateParam('tpage', String(page + 1))}
          >
            Next
          </Button>
        </div>
      )}

      <TenantDetailDialog id={activeId} open={activeId !== null} onOpenChange={(open) => !open && setActiveId(null)} />
    </div>
  );
}